import React, { useState, useEffect, useCallback } from "react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { Heading } from "../ui/Heading";
import { Image } from "../ui/Image";
import { productCustomizationApi } from "../../services/productCustomizationApi";

export interface CustomizationImageViewerProps {
  customizationId: string;
  imageUrl?: string;
  onClose: () => void;
}

export const CustomizationImageViewer: React.FC<CustomizationImageViewerProps> = ({
  customizationId,
  imageUrl,
  onClose,
}) => {
  const [image, setImage] = useState<string | undefined>(imageUrl);
  const [loading, setLoading] = useState(!imageUrl);
  const [exporting, setExporting] = useState(false);

  const loadImage = useCallback(async () => {
    try {
      setLoading(true);
      const response = await productCustomizationApi.getImage(customizationId);
      setImage(response.imageUrl);
    } catch (error: any) {
      console.error("Error loading image:", error);
    } finally {
      setLoading(false);
    }
  }, [customizationId]);

  useEffect(() => {
    if (!imageUrl) {
      loadImage();
    }
  }, [imageUrl, loadImage]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await productCustomizationApi.exportImage(customizationId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `customization-${customizationId}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error("Error exporting image:", error);
      alert(`Failed to export image: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => {
    if (image) {
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head><title>Print Customization</title></head>
            <body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;">
              <img src="${image}" style="max-width:100%;max-height:100vh;" />
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-4xl w-full max-h-[90vh] overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <Heading size="lg">Customization Preview</Heading>
            <Button variant="ghost" onClick={onClose}>×</Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading image...</div>
            </div>
          ) : image ? (
            <div className="mb-4">
              <div className="border border-gray-300 rounded overflow-hidden bg-white">
                <Image
                  src={image}
                  alt="Customization"
                  className="w-full h-auto"
                  aspectRatio="auto"
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              Image not available
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            {image && (
              <>
                <Button variant="outline" onClick={handleExport} disabled={exporting}>
                  {exporting ? "Exporting..." : "Export PNG"}
                </Button>
                <Button onClick={handlePrint}>
                  Print
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CustomizationImageViewer;

